// InvoiceExportButton.js - Récupère TOUTES les ventes du serveur avant export
import React, { useState } from 'react';
import { Button, Modal, Form, Row, Col, Alert, Spinner, Badge } from 'react-bootstrap';
import { Download, FileSpreadsheet, Calendar, Filter } from 'lucide-react';
import saleService from '../../services/saleService';
import InvoiceExportService from './InvoiceExportService';

const InvoiceExportButton = ({ activeFilters = {}, boutiqueInfo = {} }) => {
  const [showModal, setShowModal] = useState(false);
  const [exportMode, setExportMode] = useState('active'); // 'active' | 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('success');

  const dateLabels = {
    today: "Aujourd'hui",
    week: 'Cette semaine',
    month: 'Ce mois',
    all: 'Toutes les dates',
  };

  const handleShow = () => {
    setShowModal(true);
    setMessage(null);
    setExportMode('active');
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
  };

  const handleClose = () => {
    setShowModal(false);
    setMessage(null);
  };

  // ✅ Récupérer TOUTES les ventes du serveur (pas juste la page affichée)
  const fetchAllSales = async (filters) => {
    let allSales = [];
    let page = 0;
    let hasMore = true;

    while (hasMore) {
      const response = await saleService.getSalesFiltered(page, 50, filters);
      if (response.success && response.data && response.data.length > 0) {
        allSales = [...allSales, ...response.data];
        hasMore = response.pagination?.hasNext ?? false;
        page++;
      } else {
        hasMore = false;
      }
      // Sécurité : max 40 pages (2000 ventes)
      if (page >= 40) break;
    }
    return allSales;
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setMessage(null);

      let filters = {};
      let exportStartDate = null;
      let exportEndDate = null;

      if (exportMode === 'active') {
        // ✅ Mode 1 : Exporter selon le filtre actif de la liste
        filters = {
          dateFilter: activeFilters?.dateFilter || 'today',
          status: activeFilters?.statusFilter || undefined,
          search: activeFilters?.searchTerm?.trim() || undefined,
        };
      } else {
        // ✅ Mode 2 : Plage de dates personnalisée
        if (!startDate || !endDate) {
          setMessage('Veuillez sélectionner les deux dates');
          setMessageType('warning');
          setExporting(false);
          return;
        }
        if (new Date(startDate) > new Date(endDate)) {
          setMessage('La date de début doit être avant la date de fin');
          setMessageType('warning');
          setExporting(false);
          return;
        }

        exportStartDate = new Date(startDate);
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        exportEndDate = endDateTime;

        // Envoyer les dates au serveur
        filters = {
          dateFilter: 'all',
          startDate: new Date(startDate + 'T00:00:00').toISOString(),
          endDate: endDateTime.toISOString(),
        };
      }

      // ✅ Récupérer TOUTES les ventes correspondantes du serveur
      const allSales = await fetchAllSales(filters);

      if (allSales.length === 0) {
        setMessage('Aucune facture trouvée pour cette sélection');
        setMessageType('warning');
        setExporting(false);
        return;
      }

      // ✅ Appeler InvoiceExportService avec les données complètes
      const options = { boutiqueInfo };

      // Passer les dates pour le label dans le fichier Excel
      if (exportMode === 'custom') {
        options.startDate = exportStartDate;
        options.endDate = exportEndDate;
      }

      const result = InvoiceExportService.exportInvoicesToExcel(allSales, options);

      if (result.success) {
        const statsMsg = result.stats
          ? `${result.message}\nTotal: ${new Intl.NumberFormat('fr-FR').format(result.stats.totalVentes)} F CFA\nMoyenne: ${new Intl.NumberFormat('fr-FR').format(result.stats.moyenneVente)} F CFA`
          : result.message;
        setMessage(statsMsg);
        setMessageType('success');
        setTimeout(() => handleClose(), 3500);
      } else {
        setMessage(result.message);
        setMessageType('danger');
      }
    } catch (error) {
      console.error('❌ Erreur export:', error);
      setMessage("Une erreur est survenue lors de l'export");
      setMessageType('danger');
    } finally {
      setExporting(false);
    }
  };

  // Raccourcis de dates
  const setQuickDate = (type) => {
    const today = new Date();
    const start = new Date(today);

    switch (type) {
      case 'today':
        setStartDate(today.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case 'week':
        start.setDate(today.getDate() - 7);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case 'month':
        start.setDate(1);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      case 'year':
        start.setMonth(0, 1);
        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(today.toISOString().split('T')[0]);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Button
        variant="success"
        size="sm"
        onClick={handleShow}
        className="d-flex align-items-center gap-1"
      >
        <FileSpreadsheet size={16} />
        <span className="d-none d-sm-inline">Excel</span>
      </Button>

      <Modal show={showModal} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <Download size={24} className="text-success" />
            Exporter les factures
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {message && (
            <Alert variant={messageType} className="mb-3">
              {message.split('\n').map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </Alert>
          )}

          {/* ✅ Choix du mode d'export */}
          <div className="mb-3">
            <Form.Check
              type="radio"
              id="mode-active"
              name="exportMode"
              label={
                <span>
                  <Filter size={14} className="me-1" />
                  Exporter selon le filtre actif
                  <Badge bg="primary" className="ms-2" style={{ fontSize: '0.7rem' }}>
                    {dateLabels[activeFilters?.dateFilter] || "Aujourd'hui"}
                    {activeFilters?.statusFilter ? ` • ${activeFilters.statusFilter}` : ''}
                  </Badge>
                </span>
              }
              checked={exportMode === 'active'}
              onChange={() => setExportMode('active')}
            />
            <Form.Check
              type="radio"
              id="mode-custom"
              name="exportMode"
              className="mt-2"
              label={
                <span>
                  <Calendar size={14} className="me-1" />
                  Choisir une plage de dates
                </span>
              }
              checked={exportMode === 'custom'}
              onChange={() => setExportMode('custom')}
            />
          </div>

          {/* ✅ Plage de dates personnalisée */}
          {exportMode === 'custom' && (
            <>
              <div className="mb-3">
                <h6 className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>Raccourcis</h6>
                <div className="d-flex flex-wrap gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => setQuickDate('today')}>
                    Aujourd'hui
                  </Button>
                  <Button size="sm" variant="outline-primary" onClick={() => setQuickDate('week')}>
                    7 derniers jours
                  </Button>
                  <Button size="sm" variant="outline-primary" onClick={() => setQuickDate('month')}>
                    Ce mois
                  </Button>
                  <Button size="sm" variant="outline-primary" onClick={() => setQuickDate('year')}>
                    Cette année
                  </Button>
                </div>
              </div>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                      <Calendar size={14} /> Date de début
                    </Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      max={endDate || new Date().toISOString().split('T')[0]}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                      <Calendar size={14} /> Date de fin
                    </Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          <Alert variant="info" className="mb-0 py-2">
            <small>
              <strong>Le fichier contiendra :</strong> Résumé des factures, Détails des articles, Statistiques
            </small>
          </Alert>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={handleExport}
            disabled={exporting}
            className="d-flex align-items-center gap-2"
          >
            {exporting ? (
              <>
                <Spinner as="span" animation="border" size="sm" />
                Export en cours...
              </>
            ) : (
              <>
                <Download size={16} />
                Exporter
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InvoiceExportButton;